import React, {useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import {toast} from 'react-toastify';
import Button from '../../../../../../incase-domain/components/button/Button';
import DropdownMenuCompact, {
    DropdownMenuCompactRef,
    DROPDOWN_MENU_COMPACT_SUBMENU_HOVER_OPEN_CLOSE_MS,
} from '../../../../../../incase-domain/components/dropdownMenuCompact/DropdownMenuCompact';
import useCreateNewSlide from '../../../../../../service/hooks/useCreateNewSlide';
import {SLIDE_TEMPLATE_OPTIONS} from '../../../../../../service/template/slideTemplateOptions';
import {createSlidesCopyAction, updateSlidesWithoutElementsAction} from '../../../../../../store/actions/slide';
import {
    selectedSlideHiddenSelector,
    selectedSlideOnAutoFavoriteSelector,
    selectedSlideSelector,
} from '../../../../../../store/selectors/slide.selectors';
import {useRules} from '../../../../../hooks/rulesHook';

type MenuBarSlideDropdownProps = {
    slideMenuItemRef: React.RefObject<DropdownMenuCompactRef>;
    onHoverEnter: () => void;
    onOpenImportModal: () => void;
};

function MenuBarSlideDropdown({slideMenuItemRef, onHoverEnter, onOpenImportModal}: MenuBarSlideDropdownProps): JSX.Element {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const {createNewSlide} = useCreateNewSlide();
    const slideHidden = useSelector(selectedSlideHiddenSelector);
    const slideOnAutoFavorite = useSelector(selectedSlideOnAutoFavoriteSelector);
    const selectedSlide = useSelector(selectedSlideSelector);
    const {checkIfSlideIsUsedInAppliedRules} = useRules();

    const slideMenuItems = useMemo(
        () => [
            [
                {
                    label: t('menuBar.slideMenu.new'),
                    icon: 'solar:add-square-linear',
                    items: [
                        SLIDE_TEMPLATE_OPTIONS.map((option) => ({
                            label: t(option.labelKey),
                            icon: option.icon,
                            onClick: () => createNewSlide(option.template),
                            dataCy: option.template.toString(),
                        })),
                        [
                            {
                                label: t('presEditPage.importSlide'),
                                icon: 'solar:upload-minimalistic-linear',
                                onClick: onOpenImportModal,
                                dataCy: 'import-slide',
                            },
                        ],
                    ],
                },
                {
                    label: t('menuBar.slideMenu.duplicate'),
                    icon: 'solar:copy-linear',
                    onClick: () => {
                        dispatch(createSlidesCopyAction([selectedSlide]));
                    },
                },
                {
                    label: slideHidden ? t('menuBar.slideMenu.show') : t('menuBar.slideMenu.hide'),
                    icon: slideHidden ? 'solar:eye-linear' : 'solar:eye-closed-linear',
                    onClick: () => {
                        if (!selectedSlide.hidden && checkIfSlideIsUsedInAppliedRules(selectedSlide)) {
                            toast.error(t('toasts.hideSlideWithAppliedRuleError'));
                            return;
                        }
                        dispatch(
                            updateSlidesWithoutElementsAction([
                                {
                                    ...selectedSlide,
                                    hidden: !selectedSlide.hidden,
                                },
                            ])
                        );
                    },
                },
                {
                    label: slideOnAutoFavorite ? t('menuBar.slideMenu.removeBookmark') : t('menuBar.slideMenu.bookmark'),
                    icon: slideOnAutoFavorite ? 'fluent:star-off-12-filled' : 'solar:star-linear',
                    onClick: () => {
                        dispatch(
                            updateSlidesWithoutElementsAction([
                                {
                                    ...selectedSlide,
                                    onAutoFavorite: !selectedSlide.onAutoFavorite,
                                },
                            ])
                        );
                    },
                },
            ],
        ],
        [t, slideHidden, slideOnAutoFavorite, createNewSlide, dispatch, selectedSlide, onOpenImportModal, checkIfSlideIsUsedInAppliedRules]
    );

    return (
        <DropdownMenuCompact
            ref={slideMenuItemRef}
            openOnMouseEnter
            closeOnMouseExit
            closeOnMouseExitDelayMs={DROPDOWN_MENU_COMPACT_SUBMENU_HOVER_OPEN_CLOSE_MS}
            onHoverEnter={onHoverEnter}
            items={slideMenuItems}
        >
            <Button size="2xs" disableHover square variant="ghost" className="c-menuBar__button">
                {t('menuBar.slide')}
            </Button>
        </DropdownMenuCompact>
    );
}

export default React.memo(MenuBarSlideDropdown);
